import React, { ReactElement, useState } from 'react';
import { BaseEntite } from '../../../../../common/src/entities';
import { InjectionReference } from './InjectionHelper';
import ChampCascadeContent from './ChampCascadeContent';
import ChampCascadeUnResolvedContent from './ChampCascadeUnresolvedContent';

export interface PropsDeChamp<TEntite extends BaseEntite> {
    children: ReactElement;
    injectionRef: InjectionReference<any, TEntite>;
    injectionTitle?: string;
}

export interface IInjectionState<TEntite extends BaseEntite> {
    injectionResolvedValue?: any;
    injectionReferenceToResolved?: InjectionReference<any, TEntite>;
    injectionReferenceLastResolved?: InjectionReference<any, TEntite>;
    isResolving: boolean;
    injectionTrace: string;
}

export default function ChampCascade<TEntite extends BaseEntite>(props: PropsDeChamp<TEntite>): ReactElement {
    const { children, injectionRef, injectionTitle } = props;

    const [state, setState]: [IInjectionState<TEntite>, (newState: IInjectionState<TEntite>) => void] = useState<
        IInjectionState<TEntite>
    >({
        injectionResolvedValue: injectionRef.entite ? undefined : injectionRef.ref,
        injectionReferenceToResolved: injectionRef.entite ? injectionRef : { ...injectionRef, ref: undefined },
        injectionReferenceLastResolved: undefined,
        isResolving: true,
        injectionTrace: '',
    } as IInjectionState<TEntite>);

    const injectionResolveHandler = (injectionReferenceResolvedValue: any) => {
        let updatedInjectionTrace: string = state.injectionTrace;
        const nextInjectionReferenceToResolved: InjectionReference<any, TEntite> | undefined = state
            .injectionReferenceToResolved?.ref as InjectionReference<any, TEntite> | undefined;
        if (state.injectionReferenceToResolved?.notes) {
            updatedInjectionTrace = updatedInjectionTrace
                ? updatedInjectionTrace + ' -> ' + state.injectionReferenceToResolved.notes
                : state.injectionReferenceToResolved.notes;
        }
        if (state.injectionReferenceToResolved?.filterInfo?.notes) {
            updatedInjectionTrace = updatedInjectionTrace
                ? updatedInjectionTrace + ' -> ' + state.injectionReferenceToResolved.filterInfo.notes
                : state.injectionReferenceToResolved.filterInfo.notes;
        }
        setState({
            injectionResolvedValue: injectionReferenceResolvedValue,
            injectionReferenceToResolved: nextInjectionReferenceToResolved,
            injectionReferenceLastResolved: state.injectionReferenceToResolved,
            isResolving: !!nextInjectionReferenceToResolved,
            injectionTrace: updatedInjectionTrace,
        } as IInjectionState<TEntite>);
    };

    let content: ReactElement;
    if (!state.isResolving) {
        content = (
            <ChampCascadeContent
                children={children}
                injectionValue={state.injectionResolvedValue}
                injectionType={(state.injectionReferenceLastResolved as InjectionReference<any, TEntite>).modelSommaire}
                injectionTrace={state.injectionTrace}
                injectionTitle={injectionTitle}
            />
        );
    } else {
        content = (
            <ChampCascadeUnResolvedContent
                children={children}
                injectionValue={state.injectionResolvedValue}
                injectionReferenceToResolved={state.injectionReferenceToResolved as InjectionReference<any, TEntite>}
                notifyResolvedValue={injectionResolveHandler}
            />
        );
    }

    return content;
}
